import { useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { createFieldHelper } from '@cks-internal/react-base';
import { FormOfPayment } from '../types';
import { EditActivityUpdateModel } from '../schema/editActivitySchema';
import { ApiEndpoints } from '../../../../../constants/endpoints/apiEndpoints';
import { HotelBookingType } from '../../../../../globalModels/common/hotelBookingType';

type UseFormOfPaymentFieldsProps = {
  formMethods: UseFormReturn<Partial<EditActivityUpdateModel>>;
  isDisabled: boolean;
  hotelBookingType: HotelBookingType | undefined;
};

export const useFormOfPaymentFields = ({ formMethods, isDisabled, hotelBookingType }: UseFormOfPaymentFieldsProps) => {
  const formsOfPaymentWatch = useWatch({
    control: formMethods.control,
    name: 'FormsOfPayment'
  });

  const fieldHelper = createFieldHelper<FormOfPayment>(formMethods, {
    inputProps: {
      disabled: isDisabled
    }
  });

  const formFields = useMemo(() => {
    // Check if any FormOfPayment row has FormOfPaymentId equal to 12 (e-transfer)
    const isETransferSelected = formsOfPaymentWatch?.some((r) => r.FormOfPaymentId === 12);

    // Create the select field with conditional readonly
    const selectField = fieldHelper
      .selectInputQueryItems('FormOfPaymentId', { searchUrl: ApiEndpoints.selectOptions.formsOfPayment })
      .withValidation({ required: {} })
      .isNumeric();

    // Create the LastFour field with conditional validation
    let lastFourField = fieldHelper.textInput('LastFour').withInputProps({ disabled: isDisabled }).isNumeric();

    if (isETransferSelected) {
      lastFourField = lastFourField.isReadOnly(true);
    } else {
      lastFourField = lastFourField.withValidation({
        required: {},
        maxLength: { value: 4 },
        minLength: { value: 4, message: 'Last four must be exactly 4 digits' },
        pattern: { value: /^[0-9]{4}$/, message: 'Last four must be exactly 4 digits' }
      });
    }

    // Create the comment field
    const commentField = fieldHelper.textInput('Comment');

    return [selectField, lastFourField, commentField];
  }, [formsOfPaymentWatch, fieldHelper, isDisabled, hotelBookingType]);

  return {
    formFields,
    isETransferSelected: formsOfPaymentWatch?.some((r) => r.FormOfPaymentId === 12) || false
  };
};
